import { calculatePensionDeduction } from './pensionCalculations';
import { calculateLostPersonalAllowance } from './personalAllowanceCalculations';
import { calculateStudentLoanRepayment } from './studentLoanCalculations';
import { calculateNationalInsurance } from './nationalInsuranceCalculations';
import { calculateTaxFromBands } from './calculateTaxFromBands';
import { computeMarginalRate } from './computeMarginalRate';
import { adjustTaxBands } from './adjustTaxBands';
import { calculate2029SalarySacrificeNI } from './salarySacrifice2029NI';
import { validateUserInput, validateConfig } from './inputValidation';
import { round2, TAX_RELIEF_BASIC_RATE_MULTIPLIER } from './utils/calculationUtils';

/**
 * Aggregates all calculations.
 *
 * @param {Object} userInput - Contains salary, overtime, pension, studentLoan, and a "year" property.
 * @returns {Object} - Calculation results.
 */
export const calculateAll = (userInput, config) => {
  // Validate inputs before proceeding
  validateUserInput(userInput);
  validateConfig(config);

  // Determine year and configuration.
  const year = userInput.year ? String(userInput.year) : '2025';

  // Destructure relevant user inputs.
  const { salary, overtime, pension, studentLoan, studentLoanPlan } = userInput;

  // Calculate overtime addition.
  const overtimeAddition =
    overtime && overtime.include
      ? overtime.inputType === 'amount'
        ? Number(overtime.value)
        : salary * (Number(overtime.value) / 100)
      : 0;

  // Calculate pension deduction if applicable.
  const pensionDeduction =
    pension && pension.active
      ? calculatePensionDeduction(
          salary,
          pension.contribution,
          pension.inputType,
          pension.scheme,
          pension.calculationBasis,
          pension.includeOvertime,
          overtime?.value,
          config
        )
      : 0;

  // Calculate salary totals.
  const salaryPlusOvertime = salary + overtimeAddition;
  const salaryPlusOvertimeMinusPension = salaryPlusOvertime - (pension && pension.active ? pensionDeduction : 0);

  // Adjust tax bands based on the effective salary.
  const { taxBandsToUse } = adjustTaxBands(
    salaryPlusOvertimeMinusPension,
    config,
    calculateLostPersonalAllowance
  );

  // Compute lost personal allowance and effective personal allowance.
  const { lostAllowance, effectivePersonalAllowance } = calculateLostPersonalAllowance(
    salaryPlusOvertimeMinusPension,
    config
  );

  // Calculate tax details.
  const { taxPaid, bracketDetails } = calculateTaxFromBands(salaryPlusOvertimeMinusPension, taxBandsToUse);

  // Ensure a zero percent band with lost allowance info is present.
  const syntheticZeroBand = {
    rate: 0,
    taxableAmount: effectivePersonalAllowance,
    tax: 0,
    takeHome: effectivePersonalAllowance,
    lostPersonalAllowance: lostAllowance
  };

  const updatedBracketDetails = bracketDetails.length && bracketDetails[0].rate === 0
    ? [{ ...bracketDetails[0], lostPersonalAllowance: lostAllowance }, ...bracketDetails.slice(1)]
    : [syntheticZeroBand, ...bracketDetails];

  // National Insurance: adjust qualifying salary based on pension scheme.
  const niQualifyingSalary =
    pension && pension.active && pension.scheme !== 'Personal'
      ? salaryPlusOvertime - pensionDeduction
      : salaryPlusOvertime;
  const nationalInsurance = calculateNationalInsurance(niQualifyingSalary, config);

  // 2029 Mode: Additional NI on salary sacrifice contributions above £2,000
  let ni2029Surcharge = 0;
  if (
    pension &&
    pension.active &&
    pension.scheme === 'Salary Sacrifice' &&
    pension.enable2029Mode === true
  ) {
    ni2029Surcharge = calculate2029SalarySacrificeNI(
      pensionDeduction,
      salaryPlusOvertime,
      config
    );
  }

  // Student Loan calculation based on gross income (including overtime).
  const slQualifyingSalary = salaryPlusOvertime;
  const studentLoanRepayment = calculateStudentLoanRepayment(
    slQualifyingSalary,
    (studentLoan ? studentLoan.plan : null) || (studentLoanPlan ? studentLoanPlan.plan : 'none'),
    config
  );

  // Calculate take-home pay.
  const takeHome = (pension && pension.active && pension.scheme !== 'Personal')
  ? salaryPlusOvertime - taxPaid - nationalInsurance - ni2029Surcharge - studentLoanRepayment - pensionDeduction
  : salaryPlusOvertime - taxPaid - nationalInsurance - ni2029Surcharge - studentLoanRepayment;


  // Handle personal pension contributions (if scheme is Personal).
  let personalPensionRebate = 0;
  let netContribution = 0;
  let grossContribution = 0;
  if (pension && pension.active && pension.scheme === 'Personal') {
    // Input is considered net.
    netContribution = Number(pension.contribution);
    grossContribution = netContribution * TAX_RELIEF_BASIC_RATE_MULTIPLIER;
    const marginalRate = computeMarginalRate(salaryPlusOvertimeMinusPension, taxBandsToUse);
    const additionalRebate = marginalRate > 20 ? grossContribution * ((marginalRate - 20) / 100) : 0;
    personalPensionRebate = additionalRebate;
  }

  // Calculate effective tax rate
  const totalDeductions = taxPaid + nationalInsurance + ni2029Surcharge + studentLoanRepayment + (pension && pension.active ? pensionDeduction : 0);
  const effectiveTaxRate = salaryPlusOvertime > 0 ? totalDeductions / salaryPlusOvertime : 0;

  return {
    totalGrossIncome: round2(salaryPlusOvertime),
    pensionDeduction: round2(pensionDeduction),
    taxableIncome: round2(Math.max(0, salaryPlusOvertimeMinusPension - effectivePersonalAllowance)),
    taxPaid: round2(taxPaid),
    nationalInsurance: round2(nationalInsurance),
    ni2029Surcharge: round2(ni2029Surcharge),
    takeHome: round2(takeHome),
    studentLoanRepayment: round2(studentLoanRepayment),
    effectiveTaxRate: round2(effectiveTaxRate),
    configYear: year,
    effectivePersonalAllowance: round2(effectivePersonalAllowance),
    lostAllowance: round2(lostAllowance),
    overtimeAddition: round2(overtimeAddition),
    bracketDetails: updatedBracketDetails.map(row => ({
      rate: row.rate,
      taxableAmount: round2(row.taxableAmount),
      tax: round2(row.tax),
      takeHome: round2(row.takeHome),
      lostPersonalAllowance: round2(row.lostPersonalAllowance || 0)
    })),
    personalPension:
      pension && pension.active && pension.scheme === 'Personal'
        ? {
            grossContribution: round2(grossContribution),
            netContribution: round2(netContribution),
            estimatedRebate: round2(personalPensionRebate)
          }
        : undefined
  };
};
